// 9fbef606107a605d69c0edbcd8029e5d
import { HttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
// ApolloLinkTimeout is not an Apollo dependency: https://github.com/drcallaway/apollo-link-timeout
// No official replacement in Apollo Client v3.x
import ApolloLinkTimeout from 'apollo-link-timeout';
// Apollo Client v3.x createPersistedQueryLink function requires sha526 as parameter: https://www.apollographql.com/docs/react/api/link/persisted-queries/
import { isClient, isServer, generateTraceId, getHashKey } from '@tcp/core/src/utils';
import logger from '@tcp/core/src/utils/loggerInstance';
import { getBrandSwitchedState } from '@tcp/core/src/reduxStore/selectors/global.selectors';
import { getStoreRef } from '@tcp/core/src/utils/store.utils';
import { setBrandSwitched } from '@tcp/core/src/reduxStore/actions';
import ApolloClientBase from './ApolloClientBase';
import { getAPIConfig, getAmznTraceId } from '../../../../utils/utils';
import { generateSessionId } from '../../../../utils/cookie.util';
import {
  CACHE_TAG_HEADER,
  QUERY_TYPE_HEADER,
  TRACE_ID_HEADER,
  SESSION_ID_HEADER,
  CLIENT_SOURCE,
} from '../../../../constants/headers.constants';
import { getapiTimeout, getClientOptions } from './utils';

let apolloClient = null;

const requestIdentifiers = {
  traceId: null,
  sessionId: null,
};

/**
 * Class ApolloAppSyncClient which extends AWSAppSync client package
 */
class ApolloAppSyncClient extends ApolloClientBase {
  constructor(options) {
    super({ ...options });
    this.setCacheTimers();
  }

  /**
   * Generate the full cache tag string based on contextual information
   * such as Path Context, Brand Id etc.
   *
   * @param {string} cacheTagPrefix
   * @param {boolean} isContextual
   * @returns {string} Full cache tag string
   */
  generateCacheTag = (cacheTagPrefix, isContextual, apiConfig, pageName) => {
    // "module" not part of the following list - as there are dangling calls for module such as referred content etc.
    // For page level module calls - we're using "isContextual" param (details below)
    const PAGE_INFO_CACHE_TAGS = ['layout', 'seo-data', 'sub-navigation'];

    let enhancedCacheTag;

    // "isContextual" argument is sent as true for "page level module calls" and in future -
    // other calls which are bound to page context
    if (PAGE_INFO_CACHE_TAGS.indexOf(cacheTagPrefix) !== -1 || isContextual) {
      const pathContext = this.getPathContext(apiConfig, pageName);
      enhancedCacheTag = `${cacheTagPrefix}-${apiConfig.brandId}-${pathContext}`;
    } else {
      enhancedCacheTag = `${cacheTagPrefix}-${apiConfig.brandId}-${apiConfig.siteId}`;
    }
    return enhancedCacheTag;
  };

  /**
   * This function executes graphQL query
   * @param {*} query GraphQL query
   * @returns {Promise} Resolves with data or rejects with error object
   */
  executeQuery(query, apiConfig, { fetchPolicy, cacheTag, isContextual, pageName }) {
    let config;
    if (isServer()) {
      config = apiConfig;
    } else {
      config = getAPIConfig();
    }
    const headers = {};
    const pathContext = this.getPathContext(config);
    headers[CLIENT_SOURCE] = `${config?.brandId}-${pathContext}`;

    if (cacheTag) {
      const enhancedCacheTag = this.generateCacheTag(cacheTag, isContextual, config, pageName);
      headers[CACHE_TAG_HEADER] = enhancedCacheTag;
      headers[QUERY_TYPE_HEADER] = `${cacheTag}-${config.brandId}-${config.siteId}`;
    }

    return this.executeQueryPromise(query, config, { headers, fetchPolicy });
  }
}

const clientOptions = (config) => {
  const c = config || getAPIConfig();
  return getClientOptions(c);
};

const additionalOptions = (config) => {
  const apiConfig = config || getAPIConfig();
  let disablePersistedQuery = false;
  let persistedQueryLink;

  if (apiConfig.graphql_persisted_query === 'false') {
    disablePersistedQuery = true;
  }

  // Getting Session Id for Web and App (If web will be false will get fot App)
  const sessionId = generateSessionId(true) || apiConfig.reqSessionCookie || 'not-available';

  const httpLink = new HttpLink({
    headers: {},
    uri: clientOptions(apiConfig).url,
    fetchOptions: {
      mode: isClient() ? 'cors' : 'no-cors',
    },
  });

  const logRequestType = (request, headers) => {
    const { extensions } = request;
    let requestType;
    if (extensions && extensions.persistedQuery) {
      requestType = `Request: GET | sha256Hash: ${extensions.persistedQuery.sha256Hash}`;
    } else {
      requestType = 'Request: POST';
    }
    logger.info(
      `Apollo Request | Cache Tag: ${headers[CACHE_TAG_HEADER]} | Query Type: ${headers[QUERY_TYPE_HEADER]} | ${requestType}`
    );
  };

  let traceId = generateTraceId();
  const amznTraceId = getAmznTraceId(traceId);
  if (amznTraceId) {
    traceId = `${traceId}_${amznTraceId}`;
  }

  requestIdentifiers.traceId = traceId;
  requestIdentifiers.sessionId = sessionId;

  const customHeaderLink = setContext((request, { headers = null }) => {
    logRequestType(request, headers);

    return {
      headers: {
        ...headers,
        [TRACE_ID_HEADER]: traceId,
        [SESSION_ID_HEADER]: sessionId,
      },
    };
  });

  const headerLink = customHeaderLink.concat(httpLink);

  const apiTimeout = getapiTimeout(
    apiConfig.GRAPHQL_API_TIMEOUT,
    apiConfig.GRAPHQL_API_TIMEOUT_CLIENT
  );
  const timeoutLink = new ApolloLinkTimeout(apiTimeout);
  const withTimeOutLink = timeoutLink.concat(headerLink);

  if (!disablePersistedQuery) {
    persistedQueryLink = createPersistedQueryLink({
      sha256: getHashKey,
      useGETForHashedQueries: true,
    }).concat(withTimeOutLink);
  }

  return {
    link: disablePersistedQuery ? withTimeOutLink : persistedQueryLink,
    cache: new InMemoryCache(),
  };
};

const ApolloClientInstance = (apiConfig) => {
  const storeRef = getStoreRef();
  let isBrandSwitched = false;
  const store = (storeRef && storeRef.getState()) || {};
  if (Object.keys(store).length) {
    isBrandSwitched = getBrandSwitchedState(store);
  }

  if (isBrandSwitched || !apolloClient || isServer()) {
    const options = additionalOptions(apiConfig);
    apolloClient = new ApolloAppSyncClient(options);

    // Add traceId and sessionId to apolloClient
    apolloClient.sessionId = requestIdentifiers.sessionId;
    apolloClient.traceId = requestIdentifiers.traceId;

    if (isBrandSwitched) {
      storeRef.dispatch(setBrandSwitched(false));
    }
  }
  return apolloClient;
};

export default ApolloClientInstance;
